import {
  LTLExternalQuoteAssignment,
  LTLQuickQuoteResponse,
} from 'lib/api/getLTLQuickQuotes';
import { Maybe, Undef } from 'types/UtilityTypes';

import { transformLTLThreeGQuote } from '../LTL/api/transformLTLThreeGResponse';
import { ExternalQuoteStatus } from '../context/QuickQuoteContext';
import {
  LTL_ZERO_QUOTE_OUTCOME,
  LtlProviderOutcomeConfig,
  LtlZeroQuoteOutcome,
} from '../ltlProviderOutcomeConfig';
import { LTLQuote } from '../types';
import { getDefaultSelectedLTLQuotes } from './getDefaultSelectedLTLQuotes';

export type ClassifiedLTLQuoteResult = {
  quotes: LTLQuote[];
  defaultSelectedQuotes: LTLQuote[];
  zeroQuoteOutcome: Maybe<LtlZeroQuoteOutcome>;
  providerFailureMessage: Maybe<string>;
  externalQuoteURL: Maybe<string>;
  externalQuoteID: Maybe<string>;
  externalQuoteSource: Maybe<string>;
  externalQuoteLabel: Maybe<string>;
  externalQuoteStatus: Maybe<ExternalQuoteStatus>;
  externalQuoteErrorMessage: Maybe<string>;
  externalQuoteAssignment: Maybe<LTLExternalQuoteAssignment>;
  templateBody: string;
};

// The quote an LTL job saved in the TMS: its link, whether the save worked and the carrier
// assignment (3G).
export type LTLExternalQuote = {
  url: Maybe<string>;
  id: Maybe<string>;
  source: Maybe<string>;
  label: Maybe<string>;
  status: Maybe<ExternalQuoteStatus>;
  errorMessage: Maybe<string>;
  assignment: Maybe<LTLExternalQuoteAssignment>;
};

export const EMPTY_LTL_EXTERNAL_QUOTE: LTLExternalQuote = {
  url: null,
  id: null,
  source: null,
  label: null,
  status: null,
  errorMessage: null,
  assignment: null,
};

type ClassifyLTLQuoteResultOptions = {
  isThreeGActive: boolean;
  outcomeConfig: Undef<LtlProviderOutcomeConfig>;
};

// Turns a finished LTL job result into what the form shows: quotes, the zero-quote outcome,
// the TMS quote reference and the top quotes to preselect.
export function classifyLTLQuoteResult(
  value: LTLQuickQuoteResponse,
  { isThreeGActive, outcomeConfig }: ClassifyLTLQuoteResultOptions
): ClassifiedLTLQuoteResult {
  const {
    quotes,
    quoteErrors,
    quoteReplyDraftTemplate,
    externalQuoteURL,
    externalQuoteID,
    externalQuoteSource,
    externalQuoteLabel,
    externalQuoteAssignment,
    externalQuoteError,
  } = value;

  const transformedQuotes = (
    isThreeGActive
      ? quotes.map((quote: any) => transformLTLThreeGQuote(quote))
      : quotes
  ) as LTLQuote[];

  const hasNoLtlQuotes = transformedQuotes.length === 0;
  const hasQuoteErrors = (quoteErrors?.length ?? 0) > 0;
  let zeroQuoteOutcome: Maybe<LtlZeroQuoteOutcome> = null;
  let providerFailureMessage: Maybe<string> = null;

  if (outcomeConfig && hasNoLtlQuotes) {
    if (!hasQuoteErrors) {
      zeroQuoteOutcome = LTL_ZERO_QUOTE_OUTCOME.Empty;
    } else if (!outcomeConfig.quoteErrorMessageMapper) {
      zeroQuoteOutcome = LTL_ZERO_QUOTE_OUTCOME.ProviderFailure;
    } else {
      providerFailureMessage =
        outcomeConfig.quoteErrorMessageMapper(quoteErrors) ?? null;
      zeroQuoteOutcome = providerFailureMessage
        ? LTL_ZERO_QUOTE_OUTCOME.ProviderFailure
        : LTL_ZERO_QUOTE_OUTCOME.Empty;
    }
  }

  const resolvedExternalQuoteURL = getNonEmptyString(externalQuoteURL);
  const resolvedExternalQuoteSource = getNonEmptyString(externalQuoteSource);

  const externalQuoteAttempted =
    typeof externalQuoteURL === 'string' ||
    typeof externalQuoteID === 'string' ||
    typeof externalQuoteSource === 'string' ||
    typeof externalQuoteLabel === 'string';

  let externalQuoteStatus: Maybe<ExternalQuoteStatus> = null;
  if (externalQuoteError) {
    externalQuoteStatus = 'failure';
  } else if (externalQuoteAttempted) {
    externalQuoteStatus = resolvedExternalQuoteURL ? 'success' : 'failure';
  }

  return {
    quotes: transformedQuotes,
    defaultSelectedQuotes: getDefaultSelectedLTLQuotes(transformedQuotes),
    zeroQuoteOutcome,
    providerFailureMessage,
    externalQuoteURL: resolvedExternalQuoteURL,
    externalQuoteID: getNonEmptyString(externalQuoteID),
    externalQuoteSource: externalQuoteError
      ? resolvedExternalQuoteSource || externalQuoteError.source || null
      : resolvedExternalQuoteSource,
    externalQuoteLabel: getNonEmptyString(externalQuoteLabel),
    externalQuoteStatus,
    externalQuoteErrorMessage: externalQuoteError?.message ?? null,
    externalQuoteAssignment: externalQuoteAssignment ?? null,
    templateBody: quoteReplyDraftTemplate?.body ?? '',
  };
}

export function getLTLExternalQuote(
  result: ClassifiedLTLQuoteResult
): LTLExternalQuote {
  return {
    url: result.externalQuoteURL,
    id: result.externalQuoteID,
    source: result.externalQuoteSource,
    label: result.externalQuoteLabel,
    status: result.externalQuoteStatus,
    errorMessage: result.externalQuoteErrorMessage,
    assignment: result.externalQuoteAssignment,
  };
}

function getNonEmptyString(value: unknown): Maybe<string> {
  return typeof value === 'string' && value.length > 0 ? value : null;
}
